import type { RushMidSnapshot } from '@cs-shot/battle-sim';

/** The only fields the ordering reads — every RushMidSnapshot satisfies this structurally,
 *  and tests can build plain literals instead of asserting full snapshot shapes. */
export interface SnapshotOrderView {
  status: RushMidSnapshot['status'];
  round?: number;
  stateVersion?: number;
  matchId?: string;
}

// Phase order — a higher rank supersedes a lower one on a poll.
const STATUS_RANK: Record<RushMidSnapshot['status'], number> = {
  NONE: 0, PARTY: 1, OPEN: 2, BUYING: 3, PLAYING: 4, OVER: 5,
};

const roundOf = (s: SnapshotOrderView): number =>
  (s.status === 'PLAYING' || s.status === 'OVER') && s.round !== undefined ? s.round : -1;

// The match row's monotonic write counter (present on match-backed snapshots). Rank and
// round can't order two snapshots of the SAME round: two overlapping polls may resolve
// out of order over HTTP, and the stale one (read before an action submit / a teammate's
// loadout save) would overwrite the fresh one. When both sides carry the counter, only a
// same-or-newer write applies; a side without it (PARTY/OPEN) keeps the old rules.
const versionNotOlder = (next: SnapshotOrderView, prev: SnapshotOrderView): boolean =>
  next.stateVersion === undefined || prev.stateVersion === undefined
    ? true
    : next.stateVersion >= prev.stateVersion;

/**
 * A snapshot is newer iff (status, round) advances — guards against a stale poll that
 * returns an earlier round, or downgrades a settled match. NONE is authoritative
 * (cancel / left / expired): a deliberate downgrade the rank guard below would otherwise
 * reject, leaving the UI stuck on OPEN. Deliberate downgrades the client makes on
 * purpose (OVER→PARTY on "back to lobby") bypass this via a direct setSnapshot.
 *
 * Pure + framework-free so the ordering rules can be unit-tested without the controller.
 */
export const isNewerSnapshot = (next: SnapshotOrderView, prev: SnapshotOrderView | null): boolean => {
  if (!prev) return true;
  if (next.status === 'NONE') return true;
  // You're on a finished match (OVER) and a poll surfaces a DIFFERENT match: your party
  // restarted and the server already re-seated you into it (your ticket is already staked). Its
  // rank (BUYING = 3) is below OVER = 5, so the rank guard below would reject it and strand you
  // on the result screen while the buy window ticks down and the server auto-plays a default
  // loadout. A repeat of the SAME finished match is NOT newer (so polling on OVER doesn't
  // re-apply the same snapshot every second).
  if (prev.status === 'OVER') return next.matchId !== undefined && next.matchId !== prev.matchId;
  // OPEN → PARTY is an authoritative downgrade: the leader left the open game (or was pruned),
  // pulling the whole seated party back to its lobby. Without this the poll's rank guard rejects
  // it and every member but the one who left stays frozen on the open-game screen forever.
  if (next.status === 'PARTY' && prev.status === 'OPEN') return true;
  // A DIFFERENT match from the server is the server having moved us (party restarted while this
  // client was suspended, straight into the next game). Rank/round/stateVersion are per-match
  // counters — comparing them across matches froze the returning client on the finished match
  // until the new one happened to overtake its numbers.
  if (next.matchId !== undefined && prev.matchId !== undefined && next.matchId !== prev.matchId) return true;
  const ra = STATUS_RANK[next.status];
  const rb = STATUS_RANK[prev.status];
  if (ra !== rb) return ra > rb;
  if (next.status === 'PLAYING' && roundOf(next) !== roundOf(prev)) {
    return roundOf(next) > roundOf(prev);
  }
  return versionNotOlder(next, prev);
};
